/**
 * Forensic-accountant sign-in — court-exhibit auth shell (AuthLayout +
 * AuthBrandPanel) wrapping the hand-built JWT login form. Separate auth from the
 * main Clerk users (external attesters review + sign off on shared reports).
 */

import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAccountantAuth } from '../contexts/AccountantAuthContext';
import AuthLayout from '../components/auth/AuthLayout';
import { AuthField, AuthSubmit, AuthError } from '../components/auth/authForm';

export default function AccountantLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { login } = useAccountantAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      // Phase 08: external attesters land on /accountant/home (pending-work
      // dashboard). /queue/accountant is Clerk-gated and would bounce a JWT user.
      navigate('/accountant/home');
    } catch (err: any) {
      setError(err.message || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      title="Accountant sign-in"
      subtitle="Review the reports shared with you and sign off on the numbers."
      altPrompt="Need an account?"
      altLinkText="Register"
      altLinkTo="/accountant/register"
      framed={false}
      footerExtra={<BackToSite />}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <AuthError>{error}</AuthError>
        <AuthField
          id="email"
          label="Email address"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          placeholder="accountant@firm.com"
          autoComplete="email"
        />
        <AuthField
          id="password"
          label="Password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          placeholder="••••••••"
          autoComplete="current-password"
        />
        <AuthSubmit loading={loading} loadingText="Signing in…">
          Sign in
        </AuthSubmit>
      </form>
    </AuthLayout>
  );
}

export function BackToSite() {
  return (
    <Link
      to="/home"
      className="mt-4 inline-flex font-mono text-[11px] uppercase tracking-[0.12em] text-text-dim underline decoration-[var(--color-border-strong)] underline-offset-4 transition-colors hover:text-text-secondary hover:decoration-current"
    >
      Back to main site
    </Link>
  );
}
